import React, { useRef, useState, useEffect } from 'react';
import { Check, Microphone, X, CircleNotch, Warning } from '@phosphor-icons/react';
import toast from 'react-hot-toast';
import requestClient from '../../../Utility/RequestClient';
import { RootState, useAppSelector } from '../../../../store';

interface RecordButtonProps {
    onRecordingChange: (isRecording: boolean) => void;
    onVolumeChange: (volume: number) => void;
    setNewMessage: (message: string) => void;
    newMessage: string;
    i18n: any;
}

const RecordButton: React.FC<RecordButtonProps> = ({
    onRecordingChange,
    onVolumeChange,
    setNewMessage,
    newMessage,
    i18n,
}) => {
    const settings = useAppSelector((state: RootState) => state.settings);
    const hasTranscribeModel = settings?.transcribeModel && settings.transcribeModel.trim() !== '';
    const [isRecording, setIsRecording] = useState(false);
    const [isTranscribing, setIsTranscribing] = useState(false);
    const [volume, setVolume] = useState(0); // 0-255
    const mediaRecorder = useRef<MediaRecorder | null>(null);
    const chunks = useRef<Blob[]>([]);
    const audioCtx = useRef<AudioContext | null>(null);
    const analyser = useRef<AnalyserNode | null>(null);
    const dataArray = useRef<Uint8Array<ArrayBuffer> | null>(null);
    const raf = useRef<number | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const recordingSizeRef = useRef<number>(0);
    const sizeCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const isAudioContextClosed = useRef<boolean>(false);
    const maxSizeToastShown = useRef<boolean>(false);

    const MAX_RECORDING_SIZE = 24.5 * 1024 * 1024;

    useEffect(() => {
        if (onRecordingChange) {
            onRecordingChange(isRecording);
        }
    }, [isRecording, onRecordingChange]);

    useEffect(() => {
        if (onVolumeChange) {
            onVolumeChange(volume);
        }
    }, [volume, onVolumeChange]);

    useEffect(() => {
        return () => {
            if (sizeCheckIntervalRef.current) {
                clearInterval(sizeCheckIntervalRef.current);
            }

            if (audioCtx.current && !isAudioContextClosed.current) {
                try {
                    audioCtx.current.close();
                    isAudioContextClosed.current = true;
                } catch (err) {
                    console.error(i18n.translate({ id: 'error.audioContext.close', sourceName: 'Chat' }), err);
                }
            }
        };
    }, [i18n]);

    const monitor = () => {
        if (analyser.current && dataArray.current) {
            analyser.current.getByteFrequencyData(dataArray.current as Uint8Array<ArrayBuffer>);
            const avg = dataArray.current.reduce((a, b) => a + b, 0) / dataArray.current.length;
            setVolume(avg);
        }
        raf.current = requestAnimationFrame(monitor);
    };

    const startRecording = async () => {
        try {
            recordingSizeRef.current = 0;
            isAudioContextClosed.current = false;
            maxSizeToastShown.current = false;

            streamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true });

            let options = {};
            if (MediaRecorder.isTypeSupported('audio/webm')) {
                options = { mimeType: 'audio/webm' };
            } else if (MediaRecorder.isTypeSupported('audio/ogg')) {
                options = { mimeType: 'audio/ogg' };
            } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
                options = { mimeType: 'audio/mp4' };
            }

            mediaRecorder.current = new MediaRecorder(streamRef.current, options);
            chunks.current = [];

            audioCtx.current = new AudioContext();
            analyser.current = audioCtx.current.createAnalyser();
            analyser.current.fftSize = 256;
            const buffer = new ArrayBuffer(analyser.current.frequencyBinCount);
            dataArray.current = new Uint8Array(buffer) as Uint8Array<ArrayBuffer>;
            audioCtx.current.createMediaStreamSource(streamRef.current).connect(analyser.current);
            monitor();

            mediaRecorder.current.ondataavailable = (e) => {
                if (e.data.size) {
                    chunks.current.push(e.data);
                    recordingSizeRef.current += e.data.size;

                    if (recordingSizeRef.current >= MAX_RECORDING_SIZE && !maxSizeToastShown.current) {
                        maxSizeToastShown.current = true;

                        toast.error(i18n.translate({ id: 'toast.recording.maxSize', sourceName: 'Chat' }), {
                            duration: 5000,
                            icon: <Warning size={20} />,
                        });

                        stopAndSendRecording();
                    }
                }
            };

            mediaRecorder.current.start(500);
            setIsRecording(true);
        } catch (error) {
            console.error(i18n.translate({ id: 'error.recording.start', sourceName: 'Chat' }), error);
            toast.error(i18n.translate({ id: 'toast.recording.microphoneDenied', sourceName: 'Chat' }), {
                duration: 5000,
                icon: <Warning size={20} />,
            });
        }
    };

    const closeAudioContext = () => {
        if (audioCtx.current && !isAudioContextClosed.current) {
            try {
                audioCtx.current.close();
                isAudioContextClosed.current = true;
            } catch (err) {
                console.error(i18n.translate({ id: 'error.audioContext.close', sourceName: 'Chat' }), err);
            }
        }
    };

    const cancelRecording = () => {
        if (sizeCheckIntervalRef.current) {
            clearInterval(sizeCheckIntervalRef.current);
            sizeCheckIntervalRef.current = null;
        }

        if (raf.current) {
            cancelAnimationFrame(raf.current);
            raf.current = null;
        }

        if (mediaRecorder.current && mediaRecorder.current.state !== 'inactive') {
            mediaRecorder.current.stop();
        }

        closeAudioContext();

        if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
        }

        setIsRecording(false);
        recordingSizeRef.current = 0;
        maxSizeToastShown.current = false;
    };

    const stopAndSendRecording = () => {
        if (sizeCheckIntervalRef.current) {
            clearInterval(sizeCheckIntervalRef.current);
            sizeCheckIntervalRef.current = null;
        }

        if (raf.current) {
            cancelAnimationFrame(raf.current);
            raf.current = null;
        }

        closeAudioContext();

        if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
        }

        if (mediaRecorder.current && mediaRecorder.current.state !== 'inactive') {
            mediaRecorder.current.onstop = async () => {
                setIsTranscribing(true);

                try {
                    const mimeType = mediaRecorder.current?.mimeType || 'audio/webm';
                    const cleanMimeType = mimeType.split(';')[0].trim();
                    const blob = new Blob(chunks.current, { type: cleanMimeType });

                    let fileExtension = 'webm';
                    if (cleanMimeType.includes('/')) {
                        const parts = cleanMimeType.split('/');
                        if (parts.length > 1) {
                            const subtype = parts[1].trim();
                            const extensionMap: { [key: string]: string } = {
                                webm: 'webm',
                                ogg: 'ogg',
                                mp4: 'm4a',
                                mpeg: 'mp3',
                                wav: 'wav',
                            };
                            fileExtension = extensionMap[subtype] || subtype;
                        }
                    }

                    if (blob.size === 0) {
                        throw new Error('Recording is empty');
                    }

                    const formData = new FormData();
                    formData.append('file', blob, `recording.${fileExtension}`);

                    const response = await requestClient.post('/voice/transcribe', formData);

                    if (response.status !== 200) {
                        throw new Error('Error when transcribing the audio file');
                    }

                    const data = response.data;

                    setNewMessage(newMessage + data.text);
                } catch (error) {
                    toast.error(i18n.translate({ id: 'toast.recording.transcriptionError', sourceName: 'Chat' }), {
                        duration: 5000,
                        icon: <Warning size={20} />,
                    });
                    console.error(
                        i18n.translate({ id: 'toast.recording.transcriptionError', sourceName: 'Chat' }),
                        error,
                    );
                } finally {
                    setIsTranscribing(false);
                    setVolume(0);
                    setIsRecording(false);
                    recordingSizeRef.current = 0;
                    maxSizeToastShown.current = false;
                }
            };

            mediaRecorder.current.stop();
        }
    };

    if (!hasTranscribeModel) {
        return null;
    }

    return (
        <>
            {!isRecording && !isTranscribing && (
                <div className="text-layout-textcolor overflow-hidden flex justify-center items-center transition-all h-[56px] w-[56px] duration-300">
                    <button
                        className="rounded-full h-[46px] w-[46px] transition-all duration-100 text-chatInput-sendButtonColor bg-chatInput-sendButtonBackground hover:bg-chatInput-sendButtonBackground-hover border-none flex items-center justify-center"
                        onClick={startRecording}
                        aria-label={i18n.translate({ id: 'transcribeAudio', sourceName: 'Chat' })}
                        title={i18n.translate({ id: 'transcribeAudio', sourceName: 'Chat' })}
                    >
                        <Microphone size={24} />
                    </button>
                </div>
            )}

            {isTranscribing && (
                <div className="text-layout-textcolor overflow-hidden flex justify-center items-center transition-all h-[56px] w-[56px] duration-300">
                    <div
                        className="rounded-full h-[46px] w-[46px] transition-all duration-100 text-chatInput-sendButtonColor bg-chatInput-sendButtonBackground border-none flex items-center justify-center"
                        aria-label={i18n.translate({ id: 'transcribeAudio', sourceName: 'Chat' })}
                        title={i18n.translate({ id: 'transcribeAudio', sourceName: 'Chat' })}
                    >
                        <CircleNotch size={24} className="animate-spin" />
                    </div>
                </div>
            )}

            {!isTranscribing && isRecording && (
                <>
                    <div className="text-layout-textcolor overflow-hidden flex justify-center items-center transition-all h-[56px] w-[56px] duration-300">
                        <button
                            onClick={cancelRecording}
                            className="rounded-full h-[46px] w-[46px] transition-all duration-100 text-chatInput-sendButtonColor bg-chatInput-sendButtonBackground hover:bg-chatInput-sendButtonBackground-hover border-none flex items-center justify-center"
                            aria-label="Aufnahme abbrechen"
                            title="Aufnahme abbrechen"
                        >
                            <X size={24} />
                        </button>
                    </div>
                    <div className="text-layout-textcolor overflow-hidden flex justify-center items-center transition-all h-[56px] w-[56px] duration-300">
                        <button
                            onClick={stopAndSendRecording}
                            className="rounded-full h-[46px] w-[46px] transition-all duration-100 text-chatInput-sendButtonColor bg-chatInput-sendButtonBackground hover:bg-chatInput-sendButtonBackground-hover border-none flex items-center justify-center"
                            aria-label="Aufnahme beenden und senden"
                            title="Aufnahme beenden und senden"
                        >
                            <Check size={24} />
                        </button>
                    </div>
                </>
            )}
        </>
    );
};

export default RecordButton;
